<template>
  <!-- asPanel: rendered in place inside AdminSidebar.vue instead of as a popup -->
  <Teleport to="body" :disabled="asPanel">
    <div :class="asPanel ? 'contents' : 'fixed inset-0 bg-black/50 flex items-start justify-center overflow-y-auto py-10 px-4 z-50'" @click.self="!asPanel && close()">
      <div class="relative bg-surface dark:bg-surface-dark rounded-xl w-full" :class="asPanel ? 'border border-stroke dark:border-stroke-dark' : 'shadow-2xl max-w-xl'" data-tour="lesson-create-modal">

        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-stroke dark:border-stroke-dark">
          <h3 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">{{ title }}</h3>
          <button @click="close" data-tour="lesson-create-close-btn" class="p-1 text-fg-faint dark:text-fg-faint-dark rounded transition-colors">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <div class="px-6 py-5">
          <form @submit.prevent="submit" class="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <!-- Contact -->
            <div class="sm:col-span-2">
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark">Family / contact *</label>
                <button type="button" @click="toggleNewContact" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  {{ newContactMode ? 'Use existing contact' : '+ New contact' }}
                </button>
              </div>
              <template v-if="!newContactMode">
                <Combobox v-model="selectedContact" nullable>
                  <div class="relative">
                    <ComboboxInput
                      :class="inputClass + ' pr-8'"
                      :displayValue="c => c?.name ?? ''"
                      @change="contactQuery = $event.target.value"
                      placeholder="Search by contact or student name…"
                      autocomplete="off"
                    />
                    <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon class="h-4 w-4 text-fg-faint" />
                    </ComboboxButton>
                    <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0" @after-leave="contactQuery = ''">
                      <ComboboxOptions class="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg text-sm">
                        <div v-if="filteredContacts.length === 0" class="px-4 py-3 text-fg-muted dark:text-fg-muted-dark">No contacts found</div>
                        <ComboboxOption v-for="c in filteredContacts" :key="c.id" :value="c" v-slot="{ active }">
                          <li :class="['flex flex-col px-3 py-2 cursor-pointer select-none', active ? 'bg-blue-600 text-white' : 'text-fg-base dark:text-fg-base-dark']">
                            <span class="font-medium truncate">{{ c.name }}</span>
                            <span v-if="matchedStudents(c, contactQuery).length" :class="['text-xs truncate', active ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400']">
                              Parent/contact of {{ matchedStudents(c, contactQuery).map(st => st.name).join(', ') }}
                            </span>
                            <span v-if="c.email" :class="['text-xs truncate', active ? 'text-blue-200' : 'text-fg-faint']">{{ c.email }}</span>
                          </li>
                        </ComboboxOption>
                      </ComboboxOptions>
                    </TransitionRoot>
                  </div>
                </Combobox>
                <p v-if="studentPickedFromSearch" class="mt-1 text-xs text-fg-muted dark:text-fg-muted-dark">
                  Found via student {{ studentPickedFromSearch }}, so they were selected below.
                </p>
              </template>
              <template v-else>
                <div class="grid grid-cols-1 gap-2">
                  <input v-model="newContact.name" type="text" placeholder="Full name *" required :class="inputClass" />
                  <input v-model="newContact.phone" type="tel" placeholder="Phone" :class="inputClass" />
                  <input v-model="newContact.email" type="email" placeholder="Email *" required :class="inputClass" />
                </div>
              </template>
            </div>

            <!-- Student -->
            <div>
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Student</label>
              <template v-if="!newContactMode">
                <select v-model="form.student_id" :disabled="!selectedContact" :class="inputClass + ' disabled:opacity-40'">
                  <option value="">— select —</option>
                  <option v-for="s in contactStudents" :key="s.id" :value="s.id">{{ s.name }}</option>
                  <option value="__new__">+ New Student</option>
                </select>
                <input v-if="form.student_id === '__new__'" v-model="newStudentName" type="text" placeholder="New student's name"
                  :class="inputClass + ' mt-2'" />
              </template>
              <template v-else>
                <input v-model="newContact.studentName" type="text" placeholder="Student name" :class="inputClass" />
              </template>
            </div>

            <!-- Teacher (office staff only — a teacher's own lessons are always theirs) -->
            <div v-if="canManageSignups">
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Teacher *</label>
              <Combobox v-model="selectedTeacher" nullable>
                <div class="relative">
                  <ComboboxInput
                    :class="inputClass + ' pr-8'"
                    :displayValue="t => t?.name ?? ''"
                    @change="teacherQuery = $event.target.value"
                    placeholder="Search teachers…"
                    autocomplete="off"
                  />
                  <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon class="h-4 w-4 text-fg-faint" />
                  </ComboboxButton>
                  <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0" @after-leave="teacherQuery = ''">
                    <ComboboxOptions class="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg text-sm">
                      <div v-if="filteredTeachers.length === 0" class="px-4 py-3 text-fg-muted dark:text-fg-muted-dark">No teachers found</div>
                      <ComboboxOption v-for="t in filteredTeachers" :key="t.id" :value="t" v-slot="{ active }">
                        <li :class="['px-3 py-2 cursor-pointer select-none font-medium', active ? 'bg-blue-600 text-white' : 'text-fg-base dark:text-fg-base-dark']">{{ t.name }}</li>
                      </ComboboxOption>
                    </ComboboxOptions>
                  </TransitionRoot>
                </div>
              </Combobox>
            </div>

            <!-- Instrument -->
            <div>
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Instrument</label>
              <input v-model="form.instrument" type="text" placeholder="e.g. Piano" :class="inputClass" />
            </div>

            <!-- Category (MSM) — series mode, office staff only -->
            <div v-if="isSeriesMode && canManageSignups">
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Category (MSM)</label>
              <input v-model="form.category" type="text" placeholder="e.g. Private Lesson" :class="inputClass" />
            </div>

            <!-- Location -->
            <div>
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Location</label>
              <input v-model="form.location" type="text" placeholder="Optional" :class="inputClass" />
            </div>

            <!-- Date -->
            <div>
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Date *</label>
              <input v-model="form.date" type="date" required :class="inputClass" />
            </div>

            <!-- Start time -->
            <div>
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Start time *</label>
              <input v-model="form.start_time" type="time" step="300" required :class="inputClass" />
            </div>

            <!-- Duration -->
            <div>
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Duration *</label>
              <select v-model="form.duration_minutes" required :class="inputClass">
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">60 min</option>
                <option value="90">90 min</option>
              </select>
            </div>

            <!-- Repeat settings -->
            <div class="sm:col-span-2">
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Repeats</label>
              <select v-model="form.repeat_interval" :class="inputClass">
                <option v-if="!replacing" value="">Does not repeat</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly_nth_weekday">Monthly</option>
              </select>

              <div v-if="form.repeat_interval" class="mt-3 pl-3 border-l-2 border-stroke dark:border-stroke-dark grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="sm:col-span-2 text-xs text-fg-muted dark:text-fg-muted-dark">
                  {{ recurrenceSummary }}
                </div>
                <div>
                  <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">
                    End date <span class="text-fg-faint dark:text-fg-faint-dark font-normal">(optional)</span>
                  </label>
                  <input v-model="form.end_date" type="date" :class="inputClass" />
                </div>

                <!-- Notes (internal) -->
                <div class="sm:col-span-2">
                  <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Notes (internal)</label>
                  <textarea v-model="form.notes" rows="2" placeholder="Optional internal notes" :class="inputClass + ' resize-none'"></textarea>
                </div>
              </div>
            </div>

            <!-- Reschedule: when the old lessons stop (utils/series_stops.replace_series) -->
            <div v-if="replacing && prefill && !prefill.ended" class="sm:col-span-2">
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Current lessons end after</label>
              <input v-model="replacedEndDate" @input="replacedEndTouched = true" type="date" :class="inputClass" />
              <p class="mt-1 text-xs text-fg-muted dark:text-fg-muted-dark">
                The old schedule's last day. Defaults to the day before the new one starts; pick an earlier day to leave a gap.
              </p>
            </div>

            <!-- Price (office staff only — teachers get an auto-computed price from the student's active lesson series) -->
            <div v-if="canManageSignups" class="sm:col-span-2">
              <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Price per lesson *</label>
              <div class="relative">
                <span class="absolute inset-y-0 left-3 flex items-center text-fg-faint text-sm">$</span>
                <input v-model="form.price_dollars" type="number" min="0" step="0.01" placeholder="0.00"
                  :required="!form.is_comp" :disabled="form.is_comp"
                  :class="inputClass + ' pl-7 disabled:opacity-50'" />
              </div>
              <label v-if="!showAdminChoice" class="flex items-center gap-2 mt-2 text-xs text-fg-secondary dark:text-fg-secondary-dark">
                <input v-model="form.is_comp" type="checkbox" class="rounded border-stroke-strong dark:border-stroke-strong-dark" />
                Full scholarship (never billed)
              </label>
            </div>

            <!-- Office-staff branching: who to notify (has PM), or how to reach a
                 new/no-PM family (request-time-from-teacher vs. send-invite) — applies
                 whether or not the lesson repeats, since either way it may need a
                 signup/payment-setup step. Never shown to the teacher role, whose
                 equivalent behavior (auto-invite / silent auto-notify) is automatic. -->
            <div v-if="!isTeacherRole && (showActiveNotifyChoices || showAdminChoice)"
              class="sm:col-span-2 pt-3 border-t border-stroke dark:border-stroke-dark space-y-3">

              <!-- Which open signup this books (utils/signup_match.py) -->
              <div v-if="showActiveNotifyChoices && openSignups.length">
                <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Books signup</label>
                <select v-model="signupChoice" @change="signupTouched = true" :class="inputClass">
                  <option v-for="sg in openSignups" :key="sg.id" :value="sg.id">
                    {{ [sg.student || 'Student not named', sg.instrument, sg.signed_up && `signed up ${sg.signed_up}`].filter(Boolean).join(' · ') }}
                  </option>
                  <option value="none">None (not from a signup)</option>
                </select>
                <p v-if="signupChoice === 'none' && !signupTouched" class="mt-1 text-xs text-fg-muted dark:text-fg-muted-dark">
                  No signup matched on its own. Pick one if this lesson books it.
                </p>
              </div>

              <!-- Has a payment method: forced active, pick who to notify -->
              <div v-if="showActiveNotifyChoices">
                <p class="text-xs text-fg-muted dark:text-fg-muted-dark mb-1.5">Creates the series active now, billing the payment method on file. Notify:</p>
                <div class="flex items-center gap-4 text-xs text-fg-secondary dark:text-fg-secondary-dark">
                  <label class="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" v-model="notifyTeacher" class="rounded border-stroke-strong dark:border-stroke-strong-dark text-blue-600 focus:ring-blue-500" />
                    Teacher
                  </label>
                  <label class="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" v-model="notifyStudent" class="rounded border-stroke-strong dark:border-stroke-strong-dark text-blue-600 focus:ring-blue-500" />
                    Student
                  </label>
                </div>
              </div>

              <!-- No PM / new contact: request-time-from-teacher vs. send-invite-to-student -->
              <div v-if="showAdminChoice">
                <p class="text-xs text-fg-muted dark:text-fg-muted-dark mb-1.5">No payment method on file{{ newContactMode ? ' (new contact)' : '' }} — how should this be sent?</p>
                <div class="flex rounded-md border border-stroke-strong dark:border-stroke-strong-dark overflow-hidden text-xs w-fit">
                  <button type="button" @click="adminNoPmChoice = 'invite'"
                    :class="['px-3 py-1.5 font-medium', adminNoPmChoice === 'invite' ? 'bg-blue-600 text-white' : 'bg-surface dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark']">
                    Send invite to student
                  </button>
                  <button type="button" @click="adminNoPmChoice = 'request'"
                    :class="['px-3 py-1.5 font-medium border-l border-stroke-strong dark:border-stroke-strong-dark', adminNoPmChoice === 'request' ? 'bg-blue-600 text-white' : 'bg-surface dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark']">
                    Request time from teacher
                  </button>
                </div>

                <!-- How this contact was reached — feeds the same billable action log
                     (and, for a call, the time-sensitive Call Delay Bonus) as the
                     equivalent buttons on the Signups tab. Only meaningful here, since
                     this is the only branch that creates a StudentInquiry row to log it
                     against. -->
                <div class="mt-3 pt-3 border-t border-stroke dark:border-stroke-dark">
                  <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Source</label>
                  <div class="flex gap-2">
                    <select v-model="callLog.channel" :class="inputClass + ' w-40'">
                      <option value="email">Email</option>
                      <option value="call_received">Call Received</option>
                      <option value="call_back">Call Back</option>
                      <option value="in_person">In-person</option>
                      <option value="already_logged">Already Logged</option>
                    </select>
                    <input v-if="callLog.channel !== 'already_logged'" v-model="callLog.note" type="text" placeholder="Note (optional)" :class="inputClass + ' flex-1'" />
                  </div>
                </div>
              </div>

              <!-- Queued students (batch add) -->
              <div v-if="queuedStudents.length" class="space-y-1.5">
                <div v-for="(entry, i) in queuedStudents" :key="i"
                  class="flex items-center justify-between gap-2 px-3 py-2 text-xs rounded-md border border-stroke dark:border-stroke-dark bg-surface-muted dark:bg-surface-muted-dark">
                  <span class="text-fg-secondary dark:text-fg-secondary-dark truncate">{{ describeQueuedStudent(entry) }}</span>
                  <button type="button" @click="removeStudent(i)" class="text-fg-faint hover:text-red-500 flex-shrink-0">
                    <XMarkIcon class="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <button v-if="!replacing" type="button" @click="addAdditionalStudent"
                class="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                + Add additional student
              </button>
            </div>

            <!-- Error -->
            <p v-if="error" class="sm:col-span-2 text-sm text-red-600 dark:text-red-400">{{ error }}</p>

            <!-- Actions -->
            <div class="sm:col-span-2 flex justify-end gap-2 mt-1 pt-3 border-t border-stroke dark:border-stroke-dark">
              <button type="button" @click="close"
                class="px-4 py-2 text-sm font-medium rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark transition-colors">
                Cancel
              </button>
              <button type="submit" :disabled="isSubmitting || tourActive"
                :title="tourActive ? 'Disabled during the tour' : undefined"
                class="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {{ submitButtonLabel }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { XMarkIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'
import {
  Combobox, ComboboxInput, ComboboxButton, ComboboxOptions,
  ComboboxOption, TransitionRoot,
} from '@headlessui/vue'
import { useApi } from '../../composables/useApi.js'
import { localIso, notifyLessonsChanged } from '../../composables/useCalendarData.js'
import { useTour } from '../../composables/useTour.js'
import { useMultiStudentQueue } from '../../composables/useMultiStudentQueue.js'
import { dayOfWeekFromDate } from '../../utils/dateHelpers.js'

const { tourActive } = useTour()

const props = defineProps({
  initialDate:       { type: String,  default: '' },   // "YYYY-MM-DD"
  initialTime:       { type: String,  default: '' },   // "HH:MM" (24h)
  lockedTeacherName: { type: String,  default: null },  // teacher role: locked to this teacher
  lockedTeacherId:   { type: Number,  default: null },  // teacher role: their own Contact.id (needed for series creation)
  // True for anyone who can manage signups — admins AND lesson_signups-permission
  // staff, not just strict admins. Everyone else is treated as a teacher creating
  // their own lessons (locked-teacher UI, auto-resolved invite/notify behavior).
  canManageSignups:    { type: Boolean, default: false },
  preselectTeacherName: { type: String, default: null },  // office staff: pre-select this teacher in the combobox (e.g. the column clicked)
  // Render inside the admin sidebar (AdminSidebar.vue) rather than as a popup.
  asPanel: { type: Boolean, default: false },
  // Reschedule / start-again (utils/stopReasons.js openReschedule): prefilled from the old
  // series, which this one replaces. Either one identifies it.
  replacesSeriesId: { type: Number, default: null },
  replacesLessonId: { type: Number, default: null },
})

const emit = defineEmits(['close', 'saved'])

const {
  fetchApi, createLesson, createLessonSeries, getSeriesRateForStudent,
  createSubmission, suggestSlots, sendInviteDirect,
  logInteraction,
} = useApi()

// Last-resort client-side fallback, only used if the rate-lookup request itself
// fails (network error) — the normal "no active series" case is resolved
// server-side (lesson_series.py's rate-for-student: teacher's Rate, then the
// tenant's Default Rate setting), which is what activeRate.rate_per_hour_cents
// below reflects.
const FALLBACK_RATE_PER_HOUR_CENTS = 9900

const inputClass = 'w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-2 focus:ring-blue-500'

const isSubmitting  = ref(false)
const error         = ref(null)

const newContactMode = ref(false)
const newContact     = ref({ name: '', phone: '', email: '', studentName: '' })
const newStudentName = ref('')

const contactQuery    = ref('')
const teacherQuery    = ref('')
const selectedContact = ref(null)
const selectedTeacher = ref(null)

const contacts       = ref([])

const notifyTeacher = ref(true)
const notifyStudent = ref(true)
const adminNoPmChoice = ref('invite')  // 'invite' | 'request'
const callLog = ref({ channel: 'email', note: '' })

const { queued: queuedStudents, addStudent, removeStudent, clearQueue } = useMultiStudentQueue()

const form = ref({
  student_id:        '',
  instrument:         '',
  category:           'Private Lesson',
  location:           '',
  date:               props.initialDate,
  start_time:         props.initialTime,
  duration_minutes:   30,
  repeat_interval:    'weekly',   // '' | 'weekly' | 'biweekly' | 'monthly_nth_weekday'
  end_date:           '',
  notes:              '',
  price_dollars:      '',
  is_comp:            false,
})

let contactsLoading = null
function ensureContacts() {
  return (contactsLoading ||= loadContacts())
}
async function loadContacts() {
  try {
    // Office staff search the whole school roster (also needed here for the
    // teacher combobox below). Teachers are locked to their own teacher slot
    // and get a scoped list — contacts they already have lessons with —
    // rather than browsing every family in the school.
    contacts.value = await fetchApi(props.canManageSignups ? '/api/contacts/lookup' : '/api/contacts/roster')
    if (props.canManageSignups && props.preselectTeacherName) {
      selectedTeacher.value = contacts.value.find(c =>
        c.role?.toLowerCase().includes('teach') && c.name === props.preselectTeacherName
      ) ?? null
    }
  } catch { /* ignore */ }
}
ensureContacts()

// ── Reschedule / start-again ────────────────────────────────────────────────────
const replacing = computed(() => !!(props.replacesSeriesId || props.replacesLessonId))
const prefill = ref(null)   // GET /series-stops/prefill
const replacedEndDate = ref('')
const replacedEndTouched = ref(false)
const title = computed(() => !replacing.value ? 'New Lesson'
  : prefill.value?.ended ? 'Start Lessons Again' : 'Reschedule Lessons')

async function loadPrefill() {
  const q = props.replacesSeriesId ? `series_id=${props.replacesSeriesId}` : `lesson_id=${props.replacesLessonId}`
  try {
    prefill.value = await fetchApi(`/api/series-stops/prefill?${q}`)
  } catch (e) {
    error.value = e.message || 'Could not load the current lessons.'
    return
  }
  await ensureContacts()
  const p = prefill.value
  selectedContact.value = contacts.value.find(c => c.id === p.contact_id) ?? null
  if (props.canManageSignups) selectedTeacher.value = contacts.value.find(c => c.id === p.teacher_id) ?? null
  await nextTick()  // the selectedContact watcher clears the student first
  form.value.student_id = p.student_id ?? ''
  Object.assign(form.value, {
    instrument:       p.instrument || '',
    category:         p.category || form.value.category,
    location:         p.location || '',
    duration_minutes: p.duration_minutes || 30,
    is_comp:          p.billing_method === 'comp',
    repeat_interval:  'weekly',
    start_time:       form.value.start_time || p.start_time || '',
    date:             p.next_lesson_date || props.initialDate,
  })
}
if (replacing.value) loadPrefill()

// Same $/hour as the old lessons, whatever the new length.
const prefillRatePerHourCents = computed(() => {
  const p = prefill.value
  if (!p || p.price_per_lesson_cents == null || p.billing_method === 'comp') return null
  return Math.round(p.price_per_lesson_cents / (p.duration_minutes || 30) * 60)
})

watch(() => form.value.date, (d) => {
  if (!replacing.value || replacedEndTouched.value || !d) return
  const prev = new Date(d + 'T00:00:00')
  prev.setDate(prev.getDate() - 1)
  replacedEndDate.value = localIso(prev)
}, { immediate: true })

// Tenant's admin-configured default lesson price (Settings → Business Profile →
// General), converted to a per-hour figure (it's stored as a per-30-min price,
// same convention as Teacher.rate — mirrors the *2 the backend itself does in
// lesson_series.py's rate-for-student tenant_default branch). Used as the
// fallback in effectiveRatePerHourCents below instead of a stale hardcoded
// dollar amount — matters most for a brand-new contact, where there's no
// contact/student id yet to drive the per-student rate-for-student lookup at all.
const tenantDefaultRatePerHourCents = ref(null)
async function fetchDefaultRate() {
  try {
    const data = await fetchApi('/api/admin/tenant-settings/default-rate')
    if (data?.effective_cents) tenantDefaultRatePerHourCents.value = data.effective_cents * 2
  } catch { /* ignore — effectiveRatePerHourCents falls back further to FALLBACK_RATE_PER_HOUR_CENTS */ }
}
if (props.canManageSignups) fetchDefaultRate()

const filteredContacts = computed(() => {
  const q = contactQuery.value.toLowerCase()
  if (!q) return contacts.value.slice(0, 50)
  return contacts.value.filter(c =>
    c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || matchedStudents(c, q).length
  ).slice(0, 50)
})

const filteredTeachers = computed(() => {
  const teachers = contacts.value.filter(c => c.role?.toLowerCase().includes('teach'))
  const q = teacherQuery.value.toLowerCase()
  if (!q) return teachers
  return teachers.filter(t => t.name?.toLowerCase().includes(q))
})

const contactStudents = computed(() => selectedContact.value?.students || [])

// Searching by a student's name lists their contact; picking it also picks that student.
// Only when the match isn't simply the contact's own name, so "Smith" doesn't guess.
function matchedStudents(c, query) {
  const q = query.trim().toLowerCase()
  if (!q || c.name?.toLowerCase().includes(q)) return []
  return (c.students || []).filter(st => st.name?.toLowerCase().includes(q))
}
const studentPickedFromSearch = ref('')

watch(selectedContact, c => {
  newStudentName.value = ''
  const matches = c ? matchedStudents(c, contactQuery.value) : []
  form.value.student_id = matches.length === 1 ? matches[0].id : ''
  studentPickedFromSearch.value = matches.length === 1 ? matches[0].name : ''
})
watch(() => form.value.student_id, id => {
  if (!contactStudents.value.some(st => st.id === id && st.name === studentPickedFromSearch.value)) studentPickedFromSearch.value = ''
})

// ── Role / payment-method / series-mode branching ───────────────────────────────
const isSeriesMode = computed(() => !!form.value.repeat_interval)
const isTeacherRole = computed(() => !props.canManageSignups)
// A contact decision has actually been made — either "+ New contact" or an
// existing contact picked from the combobox. Before that, there's nothing to
// evaluate PM status against, so the invite/notify UI below stays hidden.
const contactChosen = computed(() => newContactMode.value || !!selectedContact.value)
// A reschedule keeps however the old lessons were paid, so it never needs an invite.
const hasValidPM = computed(() => replacing.value ||
  (!newContactMode.value && !!selectedContact.value?.stripe_customer_id && !!selectedContact.value?.stripe_pm_last4)
)
// True whenever a contact has been chosen and there's no payment method to bill
// it — a brand-new contact, or an existing contact with nothing on file. Applies
// to a one-time lesson exactly as much as a repeating series: either way billing
// it needs either an on-file PM or a payment-setup invite first.
const needsInvite = computed(() => contactChosen.value && !hasValidPM.value)
// Office staff, PM on file, repeating series → forced active-now, notify checkboxes
// shown. (A one-time lesson with a PM on file needs no series/invite machinery at
// all — it stays on the plain direct-lesson path, same as it always has.)
const showActiveNotifyChoices = computed(() => isSeriesMode.value && !isTeacherRole.value && hasValidPM.value)
// Office staff, no PM / new contact → request-time-from-teacher vs. send-invite-to-student.
// Independent of repeat: a one-time lesson for a new/no-PM contact still needs this
// choice (it's created as a series with end_date pinned to a single occurrence).
const showAdminChoice = computed(() => !isTeacherRole.value && needsInvite.value)

watch(showAdminChoice, (v) => { if (v) form.value.is_comp = false })

// Auto-pricing: look up the student's (or contact's) active lesson series and
// extrapolate a per-lesson price from its rate/hr. Teachers never see the price
// fields — this drives the value submitted on their behalf. Office staff see the
// field pre-filled from the same lookup and can still overwrite it, but it
// re-syncs whenever duration or the matched series changes.
const activeRate = ref(null)  // result of getSeriesRateForStudent, or null

let rateLookupTimer = null
async function refreshActiveRate() {
  const studentId = (form.value.student_id && form.value.student_id !== '__new__') ? Number(form.value.student_id) : null
  const contactId = (!newContactMode.value && !studentId) ? (selectedContact.value?.id ?? null) : null
  if (!studentId && !contactId) {
    activeRate.value = null
    return
  }
  const teacherId = props.canManageSignups ? selectedTeacher.value?.id : props.lockedTeacherId
  try {
    activeRate.value = await getSeriesRateForStudent({
      studentId,
      contactId,
      instrument: form.value.instrument || null,
      teacherId: teacherId || null,
    })
  } catch {
    activeRate.value = null
  }
}

watch(
  [() => form.value.student_id, selectedContact, () => form.value.instrument, selectedTeacher, newContactMode],
  () => {
    clearTimeout(rateLookupTimer)
    rateLookupTimer = setTimeout(refreshActiveRate, 300)
  },
  { immediate: true },
)

const effectiveRatePerHourCents = computed(() =>
  prefillRatePerHourCents.value ?? activeRate.value?.rate_per_hour_cents ?? tenantDefaultRatePerHourCents.value ?? FALLBACK_RATE_PER_HOUR_CENTS
)

watch(
  [effectiveRatePerHourCents, () => form.value.duration_minutes],
  ([rateCents, minutes]) => {
    form.value.price_dollars = (rateCents * Number(minutes || 0) / 60 / 100).toFixed(2)
  },
  { immediate: true },
)

// Which open signup an activate-now series books (backend utils/signup_match.py).
// Preselects the server's own match; staff can pick another or none. 'auto' =
// lookup failed, let the server decide.
const openSignups   = ref([])
const signupChoice  = ref('auto')  // signup id | 'none' | 'auto'
const signupTouched = ref(false)
let signupLookupTimer = null
async function refreshSignupMatch() {
  if (replacing.value) {  // moving existing lessons books no signup
    openSignups.value = []
    signupChoice.value = 'none'
    return
  }
  if (!showActiveNotifyChoices.value || !selectedContact.value) {
    openSignups.value = []
    signupChoice.value = 'auto'
    return
  }
  const params = new URLSearchParams({ contact_id: selectedContact.value.id })
  const studentId = form.value.student_id && form.value.student_id !== '__new__' ? Number(form.value.student_id) : null
  if (studentId) params.set('student_id', studentId)
  if (form.value.instrument) params.set('instrument', form.value.instrument)
  if (selectedTeacher.value?.id) params.set('teacher_id', selectedTeacher.value.id)
  try {
    const data = await fetchApi(`/api/lesson-series/signup-match?${params}`)
    openSignups.value = data.signups
    if (!signupTouched.value) signupChoice.value = data.match_id ?? 'none'
  } catch {
    openSignups.value = []
    signupChoice.value = 'auto'
  }
}
watch(selectedContact, () => { signupTouched.value = false })
watch(
  [selectedContact, () => form.value.student_id, () => form.value.instrument, selectedTeacher, showActiveNotifyChoices],
  () => {
    clearTimeout(signupLookupTimer)
    signupLookupTimer = setTimeout(refreshSignupMatch, 300)
  },
)

// Teachers have no scholarship checkbox — mirror the matched series' billing method instead.
watch(activeRate, (rate) => {
  if (isTeacherRole.value) form.value.is_comp = !!(rate?.found && rate.is_comp)
})

function toggleNewContact() {
  newContactMode.value = !newContactMode.value
  newContact.value = { name: '', phone: '', email: '', studentName: '' }
  selectedContact.value = null
  form.value.student_id = ''
  newStudentName.value = ''
}

function close() { emit('close') }

// Esc closes, unless something inside (an open student/contact combobox) already
// handled that Esc to close itself.
// In the sidebar, AdminSidebar.vue handles Esc (one level at a time).
const onKeydown = e => { if (e.key === 'Escape' && !e.defaultPrevented && !props.asPanel) close() }
onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const ORDINALS = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' }

// nth (1-4) or -1 for "last" occurrence of the date's weekday in its month —
// -1 is used whenever the date falls in the final 7 days of the month, so the
// series keeps landing on the true last occurrence even in shorter months.
function monthlyOrdinalFromDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const daysInMonth = new Date(y, m, 0).getDate()
  if (d + 7 > daysInMonth) return -1
  return Math.ceil(d / 7)
}

const recurrenceSummary = computed(() => {
  if (!form.value.date || !form.value.repeat_interval) return ''
  const weekday = WEEKDAY_NAMES[dayOfWeekFromDate(form.value.date)]
  if (form.value.repeat_interval === 'weekly') return `Repeats every ${weekday}`
  if (form.value.repeat_interval === 'biweekly') return `Repeats every other ${weekday}`
  if (form.value.repeat_interval === 'monthly_nth_weekday') {
    const nth = monthlyOrdinalFromDate(form.value.date)
    return `Repeats monthly on the ${nth === -1 ? 'last' : ORDINALS[nth]} ${weekday}`
  }
  return ''
})

const submitButtonLabel = computed(() => {
  if (isSubmitting.value) return 'Creating…'
  if (replacing.value) return 'Save New Schedule'
  if (needsInvite.value) {
    if (isTeacherRole.value) return 'Send Invite'
    return adminNoPmChoice.value === 'request' ? 'Send Request to Teacher' : 'Send Invite'
  }
  if (!isSeriesMode.value) return 'Create'
  return 'Create & Activate'
})

// ── Multi-student batch queue (series mode only) ─────────────────────────────────
function buildStudentSnapshot() {
  const usingNewStudent = newContactMode.value || form.value.student_id === '__new__'
  return {
    teacher:           props.canManageSignups ? selectedTeacher.value : { id: props.lockedTeacherId, name: props.lockedTeacherName },
    usingNewStudent,
    student_id:        usingNewStudent ? null : (form.value.student_id ? Number(form.value.student_id) : null),
    new_student_name:  usingNewStudent ? (newContactMode.value ? newContact.value.studentName.trim() : newStudentName.value.trim()) : null,
    instrument:        form.value.instrument.trim() || null,
    category:           form.value.category.trim() || null,
    location:           form.value.location.trim() || null,
    duration_minutes:   Number(form.value.duration_minutes),
    price_dollars:       form.value.price_dollars,
    is_comp:             form.value.is_comp,
    first_lesson_date:  form.value.date || null,
    start_time:          form.value.start_time || null,
    repeat_interval:     form.value.repeat_interval || null,
    day_of_week:         form.value.repeat_interval ? dayOfWeekFromDate(form.value.date) : null,
    repeat_nth:          form.value.repeat_interval === 'monthly_nth_weekday' ? monthlyOrdinalFromDate(form.value.date) : null,
    end_date:            form.value.end_date || null,
    notes:               form.value.notes.trim() || null,
    signup_choice:       signupChoice.value,
  }
}

function resetStudentFields() {
  form.value.student_id = ''
  form.value.instrument = ''
  form.value.location = ''
  form.value.notes = ''
  newStudentName.value = ''
  signupTouched.value = false
  if (props.canManageSignups) {
    selectedTeacher.value = null
    teacherQuery.value = ''
  }
}

function describeQueuedStudent(entry) {
  const name = entry.new_student_name
    ? `New: ${entry.new_student_name}`
    : (contactStudents.value.find(s => s.id === entry.student_id)?.name || 'Student')
  const parts = [name]
  if (entry.instrument) parts.push(entry.instrument)
  if (entry.teacher?.name) parts.push(entry.teacher.name)
  parts.push(`$${(parseFloat(entry.price_dollars) || 0).toFixed(2)}`)
  return parts.join(' · ')
}

function addAdditionalStudent() {
  const contactErr = validateContact()
  if (contactErr) { error.value = contactErr; return }
  const studentErr = validateCurrentStudent()
  if (studentErr) { error.value = studentErr; return }
  error.value = ''
  addStudent(buildStudentSnapshot())
  resetStudentFields()
}

// ── Validation ─────────────────────────────────────────────────────────────────
function validateContact() {
  if (newContactMode.value) {
    if (!newContact.value.name.trim()) return 'Please enter a contact name.'
    if (!newContact.value.email.trim()) return 'Please enter a contact email.'
  } else if (!selectedContact.value) {
    return 'Please select a contact.'
  }
  return ''
}

function validateCurrentStudent() {
  const usingNewStudent = newContactMode.value || form.value.student_id === '__new__'
  const hasStudent = newContactMode.value
    ? !!newContact.value.studentName.trim()
    : (usingNewStudent ? !!newStudentName.value.trim() : !!form.value.student_id)

  if (usingNewStudent && !newContactMode.value && !newStudentName.value.trim()) {
    return "Please enter the new student's name."
  }
  // One-time lessons always resolve a student; a series always auto-resolves one
  // server-side too, so there's no real "optional" case either way.
  if (!hasStudent) return 'Please select a student.'
  if (props.canManageSignups && !selectedTeacher.value) return 'Please select a teacher.'
  if (!form.value.date || !form.value.start_time) return 'Date and start time are required.'
  if (!form.value.is_comp && !form.value.price_dollars) return 'Please enter a price per lesson.'
  return ''
}

// ── Submit paths ───────────────────────────────────────────────────────────────
async function submitViaLessonSeries(entry, activateNow) {
  const priceCents = entry.is_comp ? 0 : Math.round(parseFloat(entry.price_dollars) * 100)
  const body = {
    teacher_id:             entry.teacher.id,
    instrument:             entry.instrument,
    category:               entry.category,
    location:               entry.location,
    duration_minutes:       entry.duration_minutes,
    day_of_week:            entry.day_of_week,
    start_time:              entry.start_time,
    first_lesson_date:      entry.first_lesson_date,
    end_date:                entry.end_date,
    repeat_interval:        entry.repeat_interval,
    repeat_nth:              entry.repeat_nth,
    price_per_lesson_cents: priceCents,
    is_comp:                entry.is_comp,
    notes:                  entry.notes,
    activate_now:            activateNow,
    notify_teacher:          activateNow ? (isTeacherRole.value ? true : notifyTeacher.value) : true,
    notify_student:          activateNow ? (isTeacherRole.value ? true : notifyStudent.value) : true,
    notify_admin:             false,
  }
  if (replacing.value && prefill.value) {
    body.replaces_series_id = prefill.value.series_id
    if (!prefill.value.ended && replacedEndDate.value) body.replaced_end_date = replacedEndDate.value
  }
  if (activateNow && entry.signup_choice === 'none') body.no_signup = true
  else if (activateNow && typeof entry.signup_choice === 'number') body.inquiry_id = entry.signup_choice
  if (newContactMode.value) {
    body.new_contact_name = newContact.value.name.trim()
    if (newContact.value.email.trim()) body.new_contact_email = newContact.value.email.trim()
    if (newContact.value.phone.trim()) body.new_contact_phone = newContact.value.phone.trim()
  } else {
    body.contact_id = selectedContact.value.id
  }
  if (entry.usingNewStudent) {
    body.new_student_name = entry.new_student_name
  } else if (entry.student_id) {
    body.student_id = entry.student_id
  }
  return createLessonSeries(body)
}

// Maps the Source picker onto the same log the Signups tab's Call/Note buttons
// use (contact_interactions). A call also marks the brand-new signup called —
// its called_at starts the Call Delay Bonus clock. The outcome value just mirrors
// the Signups tab's own Spoke/Left Voicemail split. Already Logged is a
// deliberate no-op — the specialist already recorded this contact elsewhere.
async function logCallForInquiry(inquiry) {
  const { channel, note } = callLog.value
  if (channel === 'already_logged' || !inquiry.contact_id) return
  const body = {
    call_received: { kind: 'call', outcome: 'spoke', note: note.trim() || null },
    call_back:     { kind: 'call', outcome: 'voicemail', note: note.trim() || null },
    email:         { kind: 'email', note: note.trim() || null },
    in_person:     { kind: 'note', note: `(In-person) ${note.trim() || 'Met with family in person'}` },
  }[channel]
  if (body) await logInteraction(inquiry.contact_id, body)
}

async function submitViaInquiry(entry, choice, logCall) {
  const email = newContactMode.value ? newContact.value.email.trim() : (selectedContact.value.email || '')
  if (!email) throw new Error('This contact needs an email on file to send an invite.')

  const studentName = entry.usingNewStudent
    ? entry.new_student_name
    : (contactStudents.value.find(s => s.id === entry.student_id)?.name || null)

  const inquiry = await createSubmission({
    name:         newContactMode.value ? newContact.value.name.trim() : selectedContact.value.name,
    email,
    phone:        newContactMode.value ? (newContact.value.phone.trim() || null) : (selectedContact.value.phone || null),
    student_name: studentName,
    instrument:   entry.instrument,
  })

  if (logCall) await logCallForInquiry(inquiry)

  const slot = {
    date:                    entry.first_lesson_date,
    time:                    entry.start_time,
    duration_minutes:        entry.duration_minutes,
    repeat_interval:         entry.repeat_interval,
    repeat_nth:              entry.repeat_nth,
    // A one-time (non-repeating) invite still has to go through the series/inquiry
    // pipeline — there's no separate one-off-invite mechanism — so it's created as
    // a series whose end_date is pinned to its own first lesson date, meaning
    // exactly one lesson ever materializes from it.
    end_date:                entry.repeat_interval ? entry.end_date : entry.first_lesson_date,
    category:                entry.category,
    location:                entry.location,
    price_per_lesson_cents: Math.round(parseFloat(entry.price_dollars) * 100),
    notes:                   entry.notes,
  }

  if (choice === 'request') {
    return suggestSlots(inquiry.id, { teacher_name: entry.teacher.name, slots: [slot] })
  }
  return sendInviteDirect(inquiry.id, { teacher_name: entry.teacher.name, slot })
}

async function submit() {
  error.value = null

  const contactErr = validateContact()
  if (contactErr) { error.value = contactErr; return }

  // ── No PM on file (or a brand-new contact): goes through the invite/request-
  // teacher pipeline regardless of repeat — a one-time lesson here is a series
  // with end_date pinned to a single occurrence (see submitViaInquiry). Teachers
  // get this automatically (no choice shown); office staff pick invite vs. request. ──
  if (needsInvite.value) {
    const studentErr = validateCurrentStudent()
    if (studentErr) { error.value = studentErr; return }

    isSubmitting.value = true
    try {
      const currentSnapshot = buildStudentSnapshot()
      const allStudents = [...queuedStudents.value, currentSnapshot]
      const choice = isTeacherRole.value ? 'invite' : adminNoPmChoice.value

      let lastResult = null
      for (const entry of allStudents) {
        // Teachers can't reach the signups endpoints: POST /lesson-series/ in invite
        // mode creates the signup card, emails the invite, and the time shows as a ghost.
        // A one-time lesson is pinned to a single occurrence, same as submitViaInquiry.
        if (isTeacherRole.value) {
          if (!entry.repeat_interval) entry.end_date = entry.first_lesson_date
          lastResult = await submitViaLessonSeries(entry, false)
        } else {
          // One call, logged once — on the last signup created, so the call marks
          // every sibling's new signup as called (logCallForInquiry no-ops for "already logged").
          lastResult = await submitViaInquiry(entry, choice, entry === currentSnapshot)
        }
        if (entry !== currentSnapshot) queuedStudents.value.shift()
      }
      clearQueue()
      notifyLessonsChanged(); emit('saved', { series: lastResult })
      close()
    } catch (e) {
      error.value = e.message || 'Failed to send invite.'
    } finally {
      isSubmitting.value = false
    }
    return
  }

  // ── Has a payment method on file: one-time lesson stays on the plain direct-
  // lesson path (unchanged — no series/invite machinery needed at all). ──
  if (!isSeriesMode.value) {
    const usingNewStudent = !newContactMode.value && form.value.student_id === '__new__'
    const hasStudent = newContactMode.value
      ? !!newContact.value.studentName.trim()
      : (usingNewStudent ? !!newStudentName.value.trim() : !!form.value.student_id)

    if (usingNewStudent && !newStudentName.value.trim()) {
      error.value = "Please enter the new student's name."
      return
    }
    if (!hasStudent) {
      error.value = 'Please select a student.'
      return
    }
    if (props.canManageSignups && !selectedTeacher.value) { error.value = 'Please select a teacher.'; return }
    if (!form.value.date || !form.value.start_time) { error.value = 'Date and start time are required.'; return }
    if (!form.value.is_comp && !form.value.price_dollars) { error.value = 'Please enter a price per lesson.'; return }

    const priceCents = form.value.is_comp ? 0 : Math.round(parseFloat(form.value.price_dollars) * 100)
    isSubmitting.value = true
    try {
      const body = {
        instrument:       form.value.instrument || null,
        location:         form.value.location || null,
        date:             form.value.date,
        start_time:       form.value.start_time,
        duration_minutes: Number(form.value.duration_minutes),
        price_cents:      priceCents,
        is_comp:          form.value.is_comp,
      }
      if (newContactMode.value) {
        body.new_contact_name = newContact.value.name.trim()
        body.new_contact_email = newContact.value.email.trim()
        if (newContact.value.phone.trim()) body.new_contact_phone = newContact.value.phone.trim()
        if (newContact.value.studentName.trim()) body.new_student_name = newContact.value.studentName.trim()
      } else {
        body.contact_id = selectedContact.value.id
        if (usingNewStudent) {
          body.new_student_name = newStudentName.value.trim()
        } else if (form.value.student_id) {
          body.student_id = Number(form.value.student_id)
        }
      }
      const asTeacher = props.canManageSignups ? selectedTeacher.value.name : null
      const lesson = await createLesson(body, asTeacher)
      notifyLessonsChanged(); emit('saved', { lesson })
      close()
    } catch (e) {
      error.value = e.message || 'Failed to create lesson.'
    } finally {
      isSubmitting.value = false
    }
    return
  }

  // ── Repeating series, has a payment method: forced active-now. ──
  const studentErr = validateCurrentStudent()
  if (studentErr) { error.value = studentErr; return }

  isSubmitting.value = true
  try {
    const currentSnapshot = buildStudentSnapshot()
    const allStudents = [...queuedStudents.value, currentSnapshot]

    let lastResult = null
    for (const entry of allStudents) {
      lastResult = await submitViaLessonSeries(entry, true)
      if (entry !== currentSnapshot) queuedStudents.value.shift()
    }
    clearQueue()
    notifyLessonsChanged(); emit('saved', { series: lastResult })
    close()
  } catch (e) {
    error.value = e.message || 'Failed to create lesson series.'
  } finally {
    isSubmitting.value = false
  }
}
</script>
